import { zodResolver } from '@hookform/resolvers/zod'
import { useStore } from 'effector-react'
import { ComponentType, useState } from 'react'
import { useForm } from 'react-hook-form'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { useTaxiparkQueryParams } from '~/shared/lib/useTaxiparkQueryParams'
import { formProviderModel } from '~/shared/ui/form'
import { useStorageRepeatCode } from '~/shared/ui/RepeatCode'
import {
  BasicInformationStep,
  basicInformationFormModel,
} from './BasicInformationStep'
import { ConfirmCodeStep } from './ConfirmCodeStep'
import { TIMER_REGISTRATION, TIMER_REGISTRATION_TIME } from './constant'
import { DriverLicenseStep, driverLicenseFormModel } from './DriverLicenseStep'
import { ErrorStep } from './ErrorStep'
import {
  sendCode,
  sendCodeFx,
  registration,
  registrationFx,
  validate,
} from './model'
import { PassportStep, passportFormModel } from './PassportStep'
import { goToNextStep, $currentStepFlags, goToConfirmStep } from './stepModel'
import { Steps } from './Steps'

type RegistrationProps = {
  SuccessStep?: ComponentType
  basicInformationDefaultValues?: Partial<basicInformationFormModel.FormValues>
  disabledChangePhone?: boolean
}

export const Registration = ({
  SuccessStep,
  basicInformationDefaultValues,
  disabledChangePhone,
}: RegistrationProps) => {
  const { taxiparkIdQuery, xTaxiparkKeyQuery } = useTaxiparkQueryParams()
  const { runTimer, getIsTimerWorking } =
    useStorageRepeatCode(TIMER_REGISTRATION)
  const isSubmitting = useStore(registrationFx.pending)
  const isSendCodePending = useStore(sendCodeFx.pending)

  const {
    isBasicInformationStep,
    isPassportStep,
    isDriverLicenseStep,
    isConfirmCodeStep,
    isSuccessStep,
    isErrorStep,
  } = useStore($currentStepFlags)

  const basicInformationForm = useForm<basicInformationFormModel.FormValues>({
    resolver: zodResolver(basicInformationFormModel.formSchema),
    defaultValues: basicInformationDefaultValues,
  })

  const passportForm = useForm<passportFormModel.FormValues>({
    resolver: zodResolver(passportFormModel.formSchema),
  })
  const driverLicenseForm = useForm<driverLicenseFormModel.FormValues>({
    resolver: zodResolver(driverLicenseFormModel.formSchema),
  })

  const [basicInformation, setBasicInformation] = useState<
    Partial<basicInformationFormModel.FormValues>
  >({})
  const [passport, setPassport] = useState<
    Partial<passportFormModel.FormValues>
  >({})
  const [driverLicense, setDriverLicense] = useState<
    Partial<driverLicenseFormModel.FormValues>
  >({})

  const phone = basicInformation.phone

  const handleBasicInformationOnSuccess = (
    formValues: basicInformationFormModel.FormValues,
  ) => {
    validate({
      formValues,
      taxiparkId: taxiparkIdQuery,
      formSetError:
        basicInformationForm.setError as formProviderModel.FormSetError,
      successFn: () => {
        setBasicInformation(formValues)
        goToNextStep()
      },
    })
  }

  const handlePassportOnSuccess = (
    formValues: passportFormModel.FormValues,
  ) => {
    validate({
      formValues,
      taxiparkId: taxiparkIdQuery,
      formSetError: passportForm.setError as formProviderModel.FormSetError,
      successFn: () => {
        setPassport(formValues)
        goToNextStep()
      },
    })
  }

  const warningRequestCod = () => {
    snackbarEnqueued({
      message:
        'Запросите код повторно после того как пройдет блокировка получения кода',
      variant: 'warning',
    })
  }

  const handleCodeRequest = () => {
    if (getIsTimerWorking()) {
      warningRequestCod()
      goToConfirmStep()
    } else {
      sendCode({
        phone: phone as string,
        fn: () => {
          runTimer(TIMER_REGISTRATION_TIME)
          goToConfirmStep()
        },
      })
    }
  }

  const handleDriverLicenseOnSuccess = (
    formValues: driverLicenseFormModel.FormValues,
  ) => {
    validate({
      formValues: formValues,
      taxiparkId: taxiparkIdQuery,
      formSetError:
        driverLicenseForm.setError as formProviderModel.FormSetError,
      successFn: () => {
        setDriverLicense(formValues)
        handleCodeRequest()
      },
    })
  }

  const handleSubmit = (code: string) => {
    if (!taxiparkIdQuery && !xTaxiparkKeyQuery) {
      snackbarEnqueued({
        message: 'Ошибка получения ID таксопарка, обратитесь к администратору',
        variant: 'warning',
      })
      return
    }
    registration({
      taxiparkId: taxiparkIdQuery,
      confirmCode: code,
      ...basicInformation,
      ...passport,
      ...driverLicense,
    })
  }

  return (
    <>
      {isBasicInformationStep && (
        <BasicInformationStep
          onSuccess={handleBasicInformationOnSuccess}
          form={basicInformationForm}
          disabledChangePhone={disabledChangePhone}
        />
      )}

      {isPassportStep && (
        <PassportStep onSuccess={handlePassportOnSuccess} form={passportForm} />
      )}

      {isDriverLicenseStep && (
        <DriverLicenseStep
          onSuccess={handleDriverLicenseOnSuccess}
          form={driverLicenseForm}
          isLoading={isSendCodePending}
        />
      )}

      {isConfirmCodeStep && (
        <ConfirmCodeStep
          phone={phone}
          onOk={handleSubmit}
          isSubmitting={isSubmitting}
          onCodeRepeat={handleCodeRequest}
        />
      )}

      {isSuccessStep && SuccessStep && <SuccessStep />}

      {isErrorStep && <ErrorStep />}

      {(isBasicInformationStep || isPassportStep || isDriverLicenseStep) && (
        <Steps />
      )}
    </>
  )
}
